import { useCallback, useEffect, useReducer } from 'react';
import { useFetch } from '@/hooks/use-fetch';
import { useWorkspace } from '@/contexts/workspace-context';

export interface LaunchpadStatus {
  whatsapp: boolean;
  instagram: boolean;
  facebook: boolean;
  cli: boolean;
  skills: boolean;
  allComplete: boolean;
}

type LaunchpadState = { status: LaunchpadStatus | null; loading: boolean; error: boolean };
type LaunchpadAction =
  | { type: 'start' }
  | { type: 'success'; status: LaunchpadStatus }
  | { type: 'error' };

function launchpadReducer(state: LaunchpadState, action: LaunchpadAction): LaunchpadState {
  switch (action.type) {
    case 'start':
      return { ...state, loading: true, error: false };
    case 'success':
      return { status: action.status, loading: false, error: false };
    case 'error':
      return { ...state, loading: false, error: true };
  }
}

// dispatch is not tracked by react-hooks/set-state-in-effect, so a synchronous
// start dispatch in the effect path is safe (mirrors use-dashboard-metrics.ts).
export function useLaunchpad() {
  const fetchWithAuth = useFetch();
  // GET /launchpad needs the X-Workspace-Id header, which use-fetch only sends
  // once a workspace is active. Gate the fetch on the id so it never fires
  // header-less (→ 400 WORKSPACE_ID_REQUIRED) before the context resolves.
  const { activeWorkspace } = useWorkspace();
  const wsId = activeWorkspace?.id;
  const [{ status, loading, error }, dispatch] = useReducer(launchpadReducer, {
    status: null,
    loading: true,
    error: false,
  });

  const load = useCallback(async () => {
    dispatch({ type: 'start' });
    try {
      const res = await fetchWithAuth('/launchpad');
      if (res.ok) {
        dispatch({ type: 'success', status: (await res.json()) as LaunchpadStatus });
      } else {
        dispatch({ type: 'error' });
      }
    } catch {
      dispatch({ type: 'error' });
    }
  }, [fetchWithAuth]);

  const markSkillsDone = useCallback(async () => {
    try {
      await fetchWithAuth('/launchpad/skills-done', { method: 'POST' });
    } catch {
      // POST failure is non-fatal; refetch below reflects current state
    }
    await load();
  }, [fetchWithAuth, load]);

  useEffect(() => {
    if (wsId) void load();
  }, [load, wsId]);

  return { status, loading, error, markSkillsDone };
}
